import React, {useEffect, useState, useMemo, useRef} from "react";
import api from "../../api";
import "../../styles/Tab.css";

function StockValueTab() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [visibleColumns, setVisibleColumns] = useState(new Set());
    const [showColumnToggle, setShowColumnToggle] = useState(false);
    const [columnSearchTerm, setColumnSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const tableContainerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        setProducts([]);
        setLoading(true);

        api
            .get("/v1/frontend/products?showAll=0")
            .then((res) => {
                if (cancelled) return;
                const payload = res.data ?? {};
                let rows = [];
                if (Array.isArray(payload)) rows = payload;
                else if (payload.data) {
                    if (Array.isArray(payload.data)) rows = payload.data;
                    else if (payload.data.data && Array.isArray(payload.data.data))
                        rows = payload.data.data;
                    else if (Array.isArray(payload.data.rows)) rows = payload.data.rows;
                }
                setProducts(Array.isArray(rows) ? rows : []);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setProducts([]);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);
    const formatNumber = (v) => {
        if (v == null || v === "") return "";
        const n = Number(String(v).replace(/,/g, ""));
        if (Number.isNaN(n)) return String(v);
        return (
            "$" +
            new Intl.NumberFormat(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(n)
        );
    };

    const formatPercentage = (v) => {
        if (v == null || v === "") return "";
        const n = Number(String(v).replace(/,/g, ""));
        if (Number.isNaN(n)) return String(v);
        return (
            new Intl.NumberFormat(undefined, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
            }).format(n) + '%'
        );
    };

    const formatQty = (v) => {
        if (v == null || v === "") return "";
        const n = Number(String(v).replace(/,/g, ""));
        if (Number.isNaN(n)) return String(v);
        return (
            new Intl.NumberFormat(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(n)
        );
    };

    const exclude = useMemo(
        () =>
            new Set([
                "company_id",
                "id",
                "uuid",
                "name2",
                "group",
                "category",
                "brand",
                "total_price",
                "deleted_at",
                "created_at",
                "updated_at",
                "sync_at",
                "synced_at",
                "sync_status",
                "custom_fields",
                "meta",
            ]),
        []
    );

    const allColumns = useMemo(() => {
        if (!products || products.length === 0) return [];
        const keys = Array.from(
            new Set(products.flatMap((r) => Object.keys(r || {})))
        );
        return keys.filter((k) => {
            if (!k) return false;
            return !exclude.has(k);
        });
    }, [products, exclude]);

    useEffect(() => {
        if (allColumns.length > 0 && visibleColumns.size === 0) {
            setVisibleColumns(new Set(allColumns));
        }
    }, [allColumns, visibleColumns.size]);

    const filteredProducts = useMemo(() => {
        if (!searchTerm) return products;
        const q = String(searchTerm).toLowerCase();
        return products.filter((product) => {
            return Object.values(product || {}).some((value) =>
                String(value ?? "")
                    .toLowerCase()
                    .includes(q)
            );
        });
    }, [products, searchTerm]);

    const toggleColumn = (column) => {
        setVisibleColumns((prev) => {
            const next = new Set(prev);
            if (next.has(column)) next.delete(column);
            else next.add(column);
            return next;
        });
    };

    const toggleAllColumns = () => {
        setVisibleColumns((prev) => {
            if (prev.size === allColumns.length) return new Set();
            return new Set(allColumns);
        });
    };

    const filteredColumns = useMemo(() => {
        if (!columnSearchTerm) return allColumns;
        const q = columnSearchTerm.toLowerCase();
        return allColumns.filter((c) => c.toLowerCase().includes(q));
    }, [allColumns, columnSearchTerm]);

    const totalResults = filteredProducts.length;
    const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
    const pagedProducts = filteredProducts.slice(
        (page - 1) * pageSize,
        page * pageSize
    );

    const cols = useMemo(() => {
        const visible = allColumns.filter((c) => visibleColumns.has(c));
        const balanceKeys = visible.filter((k) => /balance/i.test(k));
        if (balanceKeys.length === 0) return visible;
        const withoutBalances = visible.filter((k) => !/balance/i.test(k));
        return [...withoutBalances, ...balanceKeys];
    }, [allColumns, visibleColumns]);

    const headerLabel = (key) => {
        if (!key) return "";
        const k = String(key).toLowerCase();

        if (k === "cost_per")
            return "Cost %";

        return String(key)
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    useEffect(() => setPage(1), [searchTerm, pageSize]);

    useEffect(() => {
        const el = tableContainerRef.current;
        if (!el) return;
        if (typeof ResizeObserver === "undefined") return;
        const threshold = 900;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const w = entry.contentRect.width;
                if (w <= threshold) el.classList.add("stacked");
                else el.classList.remove("stacked");
            }
        });
        ro.observe(el);
        if (el.clientWidth <= threshold) el.classList.add("stacked");
        else el.classList.remove("stacked");
        return () => ro.disconnect();
    }, []);

    const SkeletonRow = () => (
        <tr className="skeleton-row">
            {Array.from({length: Math.max(5, cols.length || 5)}).map((_, i) => (
                <td key={i}>
                    <div className="skeleton-cell"/>
                </td>
            ))}
        </tr>
    );

    if (loading && products.length === 0) {
        return (
            <>
                <div className="products-wrapper">
                    <h2 className="products-title">Products</h2>
                    <div className="table-controls">
                        <div className="search-container">
                            <input
                                type="text"
                                placeholder="Search products..."
                                disabled
                                value={searchTerm}
                                className="search-input"
                            />
                            <span className="search-icon" aria-hidden>
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                >
                  <path
                      d="M11 4a7 7 0 1 1 0 14 7 7 0 0 1 0-14z"
                      stroke="#6b7280"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                  />
                  <path
                      d="M21 21l-4.35-4.35"
                      stroke="#6b7280"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                  />
                </svg>
              </span>
                        </div>
                        <div style={{display: "flex", gap: 8, alignItems: "center"}}>
                            <button className="column-toggle-btn disabled" disabled>
                                <svg
                                    width="22"
                                    height="22"
                                    viewBox="0 0 22 22"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <rect
                                        x="5"
                                        y="6"
                                        width="2"
                                        height="10"
                                        rx="1"
                                        fill="#6b7280"
                                    />
                                    <rect
                                        x="10"
                                        y="3"
                                        width="2"
                                        height="13"
                                        rx="1"
                                        fill="#6b7280"
                                    />
                                    <rect
                                        x="15"
                                        y="9"
                                        width="2"
                                        height="7"
                                        rx="1"
                                        fill="#6b7280"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="table-container" ref={tableContainerRef}>
                        {loading && (
                            <div className="table-loading-overlay" aria-hidden>
                                <div className="table-loading-spinner"/>
                            </div>
                        )}
                        <table className="styled-table">
                            <thead>
                            <tr>
                                {Array.from({length: 5}).map((_, i) => (
                                    <th key={i} className="table-header-cell">
                                        <div className="skeleton-cell"/>
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {Array.from({length: 5}).map((_, i) => (
                                <SkeletonRow key={i}/>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="products-wrapper">
                <h2 className="products-title">Products</h2>
                {pagedProducts.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            <svg
                                width="48"
                                height="48"
                                viewBox="0 0 24 24"
                                fill="#7B5EA3"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden
                            >
                                <circle
                                    cx="9"
                                    cy="8"
                                    r="2.2"
                                    stroke="#7B5EA3"
                                    strokeWidth="1.4"
                                    fill="#7B5EA3"
                                />
                                <path
                                    d="M3.5 20c1.8-3 4.6-4 7.5-4s5.7 1 7.5 4"
                                    stroke="#7B5EA3"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                    fill="#7B5EA3"
                                />
                                <circle
                                    cx="17"
                                    cy="8"
                                    r="1.6"
                                    stroke="#7B5EA3"
                                    strokeWidth="1.4"
                                    fill="#7B5EA3"
                                />
                            </svg>
                        </div>
                        <h3>No products found</h3>
                        <p>
                            {searchTerm
                                ? `No results for "${searchTerm}". Try adjusting your search.`
                                : "There are no products to display."}
                        </p>
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="clear-search-btn"
                                type="button"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="table-controls">
                            <div className="search-container">
                                <input
                                    type="text"
                                    placeholder="Search products..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="search-input"
                                />
                                <span className="search-icon" aria-hidden>
                  <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                  >
                    <path
                        d="M11 4a7 7 0 1 1 0 14 7 7 0 0 1 0-14z"
                        stroke="#6b7280"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                    />
                    <path
                        d="M21 21l-4.35-4.35"
                        stroke="#6b7280"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                  </svg>
                </span>
                            </div>

                            <div className="results-count">
                                {totalResults} {totalResults === 1 ? "result" : "results"}
                            </div>

                            <div className="control-group">
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                    }}
                                    className="page-size-select"
                                >
                                    {[10, 20, 50, 100].map((size) => (
                                        <option key={size} value={size}>
                                            Show {size}
                                        </option>
                                    ))}
                                </select>

                                <div className="column-toggle-container">
                                    <button
                                        onClick={() => setShowColumnToggle((v) => !v)}
                                        className="column-toggle-btn"
                                        title="Toggle Columns"
                                        aria-expanded={showColumnToggle}
                                    >
                                        <svg
                                            width="22"
                                            height="22"
                                            viewBox="0 0 22 22"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect
                                                x="5"
                                                y="6"
                                                width="2"
                                                height="10"
                                                rx="1"
                                                fill="currentColor"
                                            />
                                            <rect
                                                x="10"
                                                y="3"
                                                width="2"
                                                height="13"
                                                rx="1"
                                                fill="currentColor"
                                            />
                                            <rect
                                                x="15"
                                                y="9"
                                                width="2"
                                                height="7"
                                                rx="1"
                                                fill="currentColor"
                                            />
                                        </svg>
                                        {allColumns.length - cols.length > 0 && (
                                            <span className="column-toggle-badge">
                        {allColumns.length - cols.length}
                      </span>
                                        )}
                                    </button>

                                    {showColumnToggle && (
                                        <div
                                            className="column-toggle-dropdown"
                                            role="dialog"
                                            aria-label="Columns"
                                        >
                                            <div className="column-toggle-header">
                                                <span>Columns</span>
                                                <button
                                                    onClick={toggleAllColumns}
                                                    className="toggle-all-btn"
                                                    type="button"
                                                >
                                                    {visibleColumns.size === allColumns.length
                                                        ? "Deselect All"
                                                        : "Select All"}
                                                </button>
                                            </div>

                                            <div className="column-search-container">
                                                <input
                                                    type="text"
                                                    placeholder="Search columns..."
                                                    value={columnSearchTerm}
                                                    onChange={(e) => setColumnSearchTerm(e.target.value)}
                                                    className="column-search-input"
                                                />
                                            </div>

                                            <div className="column-list">
                                                {filteredColumns.length > 0 ? (
                                                    filteredColumns.map((column) => (
                                                        <label key={column} className="column-toggle-item">
                                                            <input
                                                                type="checkbox"
                                                                checked={visibleColumns.has(column)}
                                                                onChange={() => toggleColumn(column)}
                                                            />
                                                            <span>{headerLabel(column)}</span>
                                                        </label>
                                                    ))
                                                ) : (
                                                    <div className="no-columns-message">
                                                        No columns found
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="table-container" ref={tableContainerRef}>
                            <table className="styled-table" role="table">
                                <thead>
                                <tr className="table-header-row">
                                    {cols.map((c) => (
                                        <th className={/(qty|price|cost)/i.test(c) ? "right" : ""} key={c}>
                                            {headerLabel(c)}
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {loading
                                    ? Array.from({length: 5}).map((_, i) => (
                                        <SkeletonRow key={i}/>
                                    ))
                                    : pagedProducts.map((row) => (
                                        <tr
                                            key={row.id ?? JSON.stringify(row)}
                                            className="table-row"
                                        >
                                            {cols.map((c) => (
                                                <td className={/(qty|price|cost)/i.test(c) ? "right" : ""} key={c}
                                                    data-label={headerLabel(c)}>
                                                    {
                                                        /(cost_per)/i.test(c)
                                                            ? formatPercentage(row[c])
                                                            : /(price|cost)/i.test(
                                                                c
                                                        )
                                                            ? formatNumber(row[c])
                                                            : /(qty)/i.test(
                                                                c
                                                            )
                                                                ? formatQty(row[c])
                                                                : row[c]
                                                    }
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {totalPages > 1 && (
                                <div className="pagination-controls">
                                    <button
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="pagination-btn prev"
                                        type="button"
                                    >
                                        Previous
                                    </button>

                                    <div className="pagination-pages">
                                        {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) pageNum = i + 1;
                                            else if (page <= 3) pageNum = i + 1;
                                            else if (page >= totalPages - 2)
                                                pageNum = totalPages - 4 + i;
                                            else pageNum = page - 2 + i;
                                            if (pageNum < 1) pageNum = 1;
                                            if (pageNum > totalPages) pageNum = totalPages;
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setPage(pageNum)}
                                                    className={`pagination-page ${
                                                        page === pageNum ? "active" : ""
                                                    }`}
                                                    type="button"
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                        {totalPages > 5 && (
                                            <span className="pagination-ellipsis">...</span>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="pagination-btn next"
                                        type="button"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default StockValueTab;
